import { useNavigationTreeHashCode } from "@faire/retailer-visitor-shared/serialized-data/getNavigationTreeHashCode";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { buildLocale } from "@faire/web--source/common/localization/utils";
import { logError } from "@faire/web--source/common/logging";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import {
  useQuery as useNonPersonalizedCategoryNavigationQuery,
  useInvalidateQuery as useInvalidateNonPersonalizedCategoryNavigationQuery,
} from "@faire/web-api--source/endpoints/www/api/v2/categories/navigation/get-hooks";
import {
  useQuery as usePersonalizedCategoryNavigationQuery,
  useInvalidateQuery as useInvalidatePersonalizedCategoryNavigationQuery,
} from "@faire/web-api--source/endpoints/www/api/v2/categories/personalized/retailerToken/navigation/get-hooks";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { useCallback, useEffect, useMemo } from "react";

import {
  buildNonPersonalizedQueryParams,
  buildPersonalizedQueryParams,
} from "./buildQueryParams";

const SUPPORTED_PLATFORM_TYPES = [
  ClientPlatformType.CLIENT_MOBILE_WEB,
  ClientPlatformType.CLIENT_DESKTOP,
];
export const useNavigationQuery = (clientPlatformType: ClientPlatformType) => {
  const retailerToken = getRetailer()?.token;

  const navigationTreeHashCode = useNavigationTreeHashCode();

  const localeKey = getLocaleKey();
  const locale = buildLocale(localeKey ?? DEFAULT_LOCALE_KEY);
  const country = getSessionCountry();
  const shouldUsePersonalizedCategoryNavigationQuery =
    retailerToken !== undefined;

  const nonPersonalizedCategoryNavigationQuery = useMemo(
    () =>
      buildNonPersonalizedQueryParams({
        clientPlatformType,
        navigationTreeHashCode,
        locale,
        country,
      }),
    [clientPlatformType, navigationTreeHashCode, locale, country]
  );

  const personalizedCategoryNavigationQuery = useMemo(
    () =>
      buildPersonalizedQueryParams({
        clientPlatformType,
        navigationTreeHashCode,
        locale,
      }),
    [clientPlatformType, navigationTreeHashCode, locale]
  );

  const {
    data: nonPersonalizedCategoryNavigationData,
    isError: isNonPersonalizedCategoryNavigationError,
    error: nonPersonalizedCategoryNavigationError,
  } = useNonPersonalizedCategoryNavigationQuery(
    nonPersonalizedCategoryNavigationQuery,
    {
      enabled: !shouldUsePersonalizedCategoryNavigationQuery,
      staleTime: Infinity,
    }
  );

  const {
    data: personalizedCategoryNavigationData,
    isError: isPersonalizedCategoryNavigationError,
    error: personalizedCategoryNavigationError,
  } = usePersonalizedCategoryNavigationQuery(
    retailerToken ?? "",
    personalizedCategoryNavigationQuery,
    {
      enabled: shouldUsePersonalizedCategoryNavigationQuery,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (isNonPersonalizedCategoryNavigationError) {
      logError(nonPersonalizedCategoryNavigationError);
    }
  }, [
    isNonPersonalizedCategoryNavigationError,
    nonPersonalizedCategoryNavigationError,
  ]);

  useEffect(() => {
    if (isPersonalizedCategoryNavigationError) {
      logError(personalizedCategoryNavigationError);
    }
  }, [
    isPersonalizedCategoryNavigationError,
    personalizedCategoryNavigationError,
  ]);

  const invalidateNonPersonalizedCategoryNavigationQuery =
    useInvalidateNonPersonalizedCategoryNavigationQuery();
  const invalidatePersonalizedCategoryNavigationQuery =
    useInvalidatePersonalizedCategoryNavigationQuery();

  const invalidateNavigationQueries = useCallback(async () => {
    for (const supportedPlatformType of SUPPORTED_PLATFORM_TYPES) {
      invalidateNonPersonalizedCategoryNavigationQuery({
        ...nonPersonalizedCategoryNavigationQuery,
        clientPlatformType: supportedPlatformType,
      });
      invalidatePersonalizedCategoryNavigationQuery(retailerToken ?? "", {
        ...personalizedCategoryNavigationQuery,
        clientPlatformType: supportedPlatformType,
      });
    }
  }, [
    retailerToken,
    invalidateNonPersonalizedCategoryNavigationQuery,
    nonPersonalizedCategoryNavigationQuery,
    invalidatePersonalizedCategoryNavigationQuery,
    personalizedCategoryNavigationQuery,
  ]);

  const navigationLinkGroup = useMemo(() => {
    const navigationLinkData = shouldUsePersonalizedCategoryNavigationQuery
      ? personalizedCategoryNavigationData
      : nonPersonalizedCategoryNavigationData;
    return navigationLinkData?.main_navigation_link_group ?? [];
  }, [
    nonPersonalizedCategoryNavigationData,
    personalizedCategoryNavigationData,
    shouldUsePersonalizedCategoryNavigationQuery,
  ]);

  return {
    navigationLinkGroup,
    invalidateNavigationQueries,
  };
};
